/**
 * Canonical COMPANY branding accessors (Phase 1).
 * Product fallback (Mafert logo asset) is separate — see constants/brandingDisplay.js
 * and docs/company-settings-branding-contract.md.
 */

import { uiT } from './uiTranslate';

function trimStr(v) {
  return String(v ?? '').trim();
}

/**
 * Desktop / light shell company logo.
 * company logo_url → product fallback
 *
 * @param {Record<string, unknown> | null | undefined} branding
 * @param {string} productFallbackSrc
 * @returns {string}
 */
export function resolveDesktopCompanyLogoSrc(branding, productFallbackSrc) {
  const url = trimStr(branding?.logo_url);
  return url || productFallbackSrc;
}

/**
 * Print / PDF-oriented company logo.
 * logo_print_url → logo_url → product fallback
 *
 * @param {Record<string, unknown> | null | undefined} branding
 * @param {string} productFallbackSrc
 * @returns {string}
 */
export function resolvePrintCompanyLogoSrc(branding, productFallbackSrc) {
  const printUrl = trimStr(branding?.logo_print_url);
  if (printUrl) return printUrl;
  const url = trimStr(branding?.logo_url);
  return url || productFallbackSrc;
}

/**
 * Mobile dark-chrome company logo choice (pure; load-error helpers live in the hook).
 * mobile_logo_url → logo_url → product fallback
 *
 * @param {Record<string, unknown> | null | undefined} branding
 * @param {string} productFallbackSrc
 * @param {{ mobileFailed?: boolean, mainFailed?: boolean }} [opts]
 * @returns {{ src: string, source: 'mobile' | 'main' | 'product' }}
 */
export function resolveMobileCompanyLogoSrc(branding, productFallbackSrc, opts = {}) {
  const { mobileFailed = false, mainFailed = false } = opts;
  const mobileUrl = trimStr(branding?.mobile_logo_url);
  const mainUrl = trimStr(branding?.logo_url);
  if (mobileUrl && !mobileFailed) return { src: mobileUrl, source: 'mobile' };
  if (mainUrl && !mainFailed) return { src: mainUrl, source: 'main' };
  return { src: productFallbackSrc, source: 'product' };
}

/**
 * Normalized company identity from company_settings_print (or compatible row).
 * Does not invent product names.
 *
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {{
 *   companyName: string,
 *   address: string,
 *   identification: string,
 *   contact: string,
 *   email: string,
 *   phone: string,
 *   website: string,
 * }}
 */
export function getCompanyIdentity(row) {
  const r = row && typeof row === 'object' ? row : {};
  const companyName = trimStr(r.company_name);

  const street = trimStr(r.street);
  const zip = trimStr(r.zip);
  const city = trimStr(r.city);
  const country = trimStr(r.country);
  const line1 = street;
  const line2 = [zip, city].filter(Boolean).join(' ');
  const address = [line1, line2, country].filter(Boolean).join(', ');

  const parts = [];
  if (trimStr(r.ico)) parts.push(`${uiT('CompanySettings.Label.Ico')}: ${trimStr(r.ico)}`);
  if (trimStr(r.dic)) parts.push(`${uiT('CompanySettings.Label.Dic')}: ${trimStr(r.dic)}`);
  if (trimStr(r.ic_dph)) parts.push(`${uiT('CompanySettings.Label.IcDph')}: ${trimStr(r.ic_dph)}`);
  const identification = parts.join(' · ');

  const email = trimStr(r.email);
  const phone = trimStr(r.phone);
  const website = trimStr(r.website);
  const contact = [phone, email, website].filter(Boolean).join(' · ');

  return { companyName, address, identification, contact, email, phone, website };
}
